import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import { ChevronRight, Check, CheckCheck, Plus, Trash2, X, ListTree, GripVertical } from 'lucide-react';

// ---------------------------------------------------------------------------
// Sub-tasks — a multi-level checklist under a task.
//   * Simple items: title + a done checkbox.
//   * Completing all direct sub-tasks auto-completes the parent (server side);
//     un-completing one reverts the parent to "In Progress".
//   * `compact` renders the lean inline version used inside a list row
//     (toggle + nested expand only — no add/rename/delete/drag chrome).
//   * Full mode supports native HTML5 drag-and-drop to reorder siblings or
//     re-nest a sub-task under another (guarded against self/descendant drops).
//
// NOTE: the tree is flattened to an ordered list of visible rows in plain JS
// and rendered via a single non-recursive row component. We deliberately avoid
// a self-referencing JSX component (the dev visual-edits babel plugin cannot
// instrument recursive components).
// ---------------------------------------------------------------------------

const buildTree = (items) => {
  const byParent = {};
  (items || []).forEach((it) => {
    const key = it.parent_id || 'root';
    (byParent[key] = byParent[key] || []).push(it);
  });
  Object.values(byParent).forEach((arr) => arr.sort((a, b) => (a.position || 0) - (b.position || 0)));
  return byParent;
};

// Depth-first list of the rows that should be visible given collapse state.
const flattenVisible = (byParent, collapsed, parentId = 'root', depth = 0, out = []) => {
  const kids = byParent[parentId] || [];
  kids.forEach((node) => {
    const hasKids = (byParent[node.id] || []).length > 0;
    out.push({ node, depth, hasKids });
    if (hasKids && !collapsed.has(node.id)) {
      flattenVisible(byParent, collapsed, node.id, depth + 1, out);
    }
  });
  return out;
};

// id + all descendant ids (used to block invalid drops).
const descendantIds = (byParent, id) => {
  const out = new Set([id]);
  const stack = [id];
  while (stack.length) {
    const cur = stack.pop();
    (byParent[cur] || []).forEach((n) => { out.add(n.id); stack.push(n.id); });
  }
  return out;
};

const SubtaskRow = ({
  node, depth, hasKids, isCollapsed, canEdit, compact,
  isEditing, editValue, setEditValue, onSubmitEdit, onCancelEdit, onOpenEdit,
  isAdding, addValue, setAddValue, onSubmitAdd, onCancelAdd, onOpenAdd,
  onToggle, onToggleCollapse, onDelete,
  // drag & drop
  draggable, isDragging, dropHint,
  onDragStart, onDragEnd, onBeforeOver, onBeforeDrop, onRowOver, onRowLeave, onRowDrop,
}) => (
  <div data-testid="subtask-row" className={isDragging ? 'opacity-40' : ''}>
    {/* Insert-before drop line (reorder within the row's parent) */}
    {canEdit && !compact && (
      <div data-testid="subtask-drop-before"
        onDragOver={(e) => onBeforeOver(e, node)} onDrop={(e) => { e.preventDefault(); onBeforeDrop(node); }}
        style={{ marginLeft: `${depth * 18}px` }}
        className={`h-1.5 rounded-full transition-colors ${dropHint === 'before' ? 'bg-[var(--n-accent)]' : 'bg-transparent'}`} />
    )}
    <div
      draggable={draggable}
      onDragStart={(e) => onDragStart(e, node)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => onRowOver(e, node)}
      onDragLeave={onRowLeave}
      onDrop={(e) => { e.preventDefault(); onRowDrop(node); }}
      className={`group flex items-center gap-1.5 rounded-lg py-1 pr-1 transition-colors ${dropHint === 'nest' ? 'bg-[var(--n-accent-bg)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'hover:bg-[var(--n-canvas-2)]'}`}
      style={{ paddingLeft: `${depth * 18}px` }}>
      {canEdit && !compact && (
        <span data-testid="subtask-drag-handle" title="Drag to reorder or re-nest" aria-hidden="true"
          className="grid h-5 w-4 shrink-0 cursor-grab place-items-center text-[var(--n-muted-2)] opacity-0 transition-opacity group-hover:opacity-100 active:cursor-grabbing">
          <GripVertical size={13} />
        </span>
      )}
      {hasKids ? (
        <button type="button" data-testid="subtask-expand" title={isCollapsed ? 'Expand' : 'Collapse'}
          onClick={() => onToggleCollapse(node.id)}
          className="n-focus grid h-5 w-5 shrink-0 place-items-center rounded text-[var(--n-muted-2)] hover:text-[var(--n-ink)]">
          <ChevronRight size={14} className={`transition-transform ${isCollapsed ? '' : 'rotate-90'}`} />
        </button>
      ) : <span className="w-5 shrink-0" aria-hidden="true" />}

      <button type="button" data-testid="subtask-toggle"
        onClick={() => onToggle(node)}
        className={`n-focus grid h-[17px] w-[17px] shrink-0 place-items-center rounded-md border transition-colors
          ${node.is_completed ? 'border-[var(--n-accent)] bg-[var(--n-accent)] text-white' : 'border-[var(--n-muted-2)] hover:border-[var(--n-accent)]'}`}
        aria-label={node.is_completed ? 'Mark incomplete' : 'Mark complete'} aria-pressed={node.is_completed}>
        {node.is_completed && <Check size={11} strokeWidth={3} />}
      </button>

      {isEditing ? (
        <input autoFocus value={editValue} data-testid="subtask-rename-input"
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onSubmitEdit(node.id); else if (e.key === 'Escape') onCancelEdit(); }}
          onBlur={() => onSubmitEdit(node.id)}
          className="n-focus min-w-0 flex-1 rounded border border-[var(--n-border)] bg-[var(--n-surface)] px-1.5 py-0.5 text-[13px] text-[var(--n-ink)]" />
      ) : (
        <button type="button" data-testid="subtask-title" disabled={!canEdit || compact}
          onClick={() => canEdit && !compact && onOpenEdit(node)}
          className={`min-w-0 flex-1 truncate text-left text-[13px] ${node.is_completed ? 'text-[var(--n-muted-2)] line-through' : 'text-[var(--n-ink)]'} ${canEdit && !compact ? 'cursor-text' : 'cursor-default'}`}>
          {node.title}
        </button>
      )}

      {hasKids && (
        <span data-testid="subtask-child-count" className="shrink-0 rounded-full bg-[var(--n-canvas-2)] px-1.5 py-0.5 text-[10.5px] tabular-nums text-[var(--n-muted)]">
          {node.child_done}/{node.child_total}
        </span>
      )}

      {canEdit && !compact && (
        <div className="flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100">
          <button type="button" data-testid="subtask-add-child" title="Add sub-item"
            onClick={() => onOpenAdd(node.id)}
            className="n-focus grid h-6 w-6 place-items-center rounded text-[var(--n-muted-2)] hover:text-[var(--n-accent)]"><Plus size={14} /></button>
          <button type="button" data-testid="subtask-delete" title="Delete"
            onClick={() => onDelete(node)}
            className="n-focus grid h-6 w-6 place-items-center rounded text-[var(--n-muted-2)] hover:text-[var(--n-danger)]"><Trash2 size={13} /></button>
        </div>
      )}
    </div>

    {isAdding && canEdit && !compact && (
      <div className="flex items-center gap-1.5 py-1" style={{ paddingLeft: `${(depth + 1) * 18 + 20}px` }}>
        <input autoFocus value={addValue} placeholder="Sub-item title…" data-testid="subtask-add-child-input"
          onChange={(e) => setAddValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onSubmitAdd(node.id); else if (e.key === 'Escape') onCancelAdd(); }}
          className="n-focus min-w-0 flex-1 rounded-md border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1 text-[13px] text-[var(--n-ink)]" />
        <button type="button" onClick={() => onSubmitAdd(node.id)} className="n-focus rounded-md p-1 text-[var(--n-accent)] hover:bg-[var(--n-accent-bg)]"><Check size={15} /></button>
        <button type="button" onClick={onCancelAdd} className="n-focus rounded-md p-1 text-[var(--n-muted-2)] hover:text-[var(--n-danger)]"><X size={15} /></button>
      </div>
    )}
  </div>
);

const TaskSubtasks = ({ taskId, canEdit = false, compact = false, allowAdd = false, refreshKey = 0, onTaskSync }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [done, setDone] = useState(0);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [addingUnder, setAddingUnder] = useState(null); // node id
  const [addValue, setAddValue] = useState('');
  const [rootValue, setRootValue] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [dragId, setDragId] = useState(null);
  const [dropHint, setDropHint] = useState(null); // { id, mode: 'before' | 'nest' }
  const dragIdRef = useRef(null); // synchronous mirror of dragId for event handlers
  const syncRef = useRef(onTaskSync);
  useEffect(() => { syncRef.current = onTaskSync; }, [onTaskSync]);

  const apply = useCallback((data) => {
    if (!data) return;
    setItems(data.items || []);
    setTotal(data.total || 0);
    setDone(data.done || 0);
    if (data.task && syncRef.current) syncRef.current(data.task);
  }, []);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try { const { data } = await notesApi.get(`/notes/tasks/tasks/${taskId}/subtasks`); apply(data); }
    catch { setItems([]); setTotal(0); setDone(0); }
    finally { setLoading(false); }
  }, [taskId, apply]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const byParent = useMemo(() => buildTree(items), [items]);
  const rows = useMemo(() => flattenVisible(byParent, collapsed), [byParent, collapsed]);
  const rootCount = (byParent.root || []).length;

  const toggleCollapse = useCallback((id) => setCollapsed((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  }), []);

  const toggle = useCallback(async (node) => {
    try { const { data } = await notesApi.patch(`/notes/tasks/tasks/${taskId}/subtasks/${node.id}`, { is_completed: !node.is_completed }); apply(data); }
    catch { /* noop */ }
  }, [taskId, apply]);

  // Bulk complete / reopen every sub-task in one click.
  const setAll = useCallback(async (target) => {
    try { const { data } = await notesApi.post(`/notes/tasks/tasks/${taskId}/subtasks/set-all`, { is_completed: target }); apply(data); }
    catch { /* noop */ }
  }, [taskId, apply]);
  const allDone = total > 0 && done >= total;

  const submitAdd = useCallback(async (parentId) => {
    const title = (addValue || '').trim();
    if (!title) { setAddingUnder(null); setAddValue(''); return; }
    try { const { data } = await notesApi.post(`/notes/tasks/tasks/${taskId}/subtasks`, { title, parent_id: parentId }); apply(data); }
    catch { /* noop */ }
    setAddValue(''); setAddingUnder(null);
    setCollapsed((s) => { const n = new Set(s); n.delete(parentId); return n; });
  }, [addValue, taskId, apply]);

  const submitRoot = useCallback(async () => {
    const title = (rootValue || '').trim();
    if (!title) return;
    try { const { data } = await notesApi.post(`/notes/tasks/tasks/${taskId}/subtasks`, { title, parent_id: null }); apply(data); }
    catch { /* noop */ }
    setRootValue('');
  }, [rootValue, taskId, apply]);

  const submitEdit = useCallback(async (id) => {
    const title = (editValue || '').trim();
    setEditingId(null);
    const cur = items.find((i) => i.id === id);
    if (!title || (cur && cur.title === title)) return;
    try { const { data } = await notesApi.patch(`/notes/tasks/tasks/${taskId}/subtasks/${id}`, { title }); apply(data); }
    catch { /* noop */ }
  }, [editValue, items, taskId, apply]);

  const del = useCallback(async (node) => {
    try { const { data } = await notesApi.delete(`/notes/tasks/tasks/${taskId}/subtasks/${node.id}`); apply(data); }
    catch { /* noop */ }
  }, [taskId, apply]);

  const openAdd = useCallback((id) => { setAddingUnder(id); setAddValue(''); }, []);
  const openEdit = useCallback((node) => { setEditingId(node.id); setEditValue(node.title); }, []);
  const cancelAdd = useCallback(() => { setAddingUnder(null); setAddValue(''); }, []);
  const cancelEdit = useCallback(() => setEditingId(null), []);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  // ---- Drag & drop ---------------------------------------------------------
  const doMove = useCallback(async (draggedId, targetNode, mode) => {
    if (!draggedId || !targetNode || draggedId === targetNode.id) return;
    const banned = descendantIds(byParent, draggedId); // dragged + descendants
    let toParent;
    let toIndex;
    if (mode === 'nest') {
      if (banned.has(targetNode.id)) return; // can't nest into itself/own descendant
      toParent = targetNode.id;
      toIndex = 9999; // append as last child (server clamps)
    } else { // 'before' — reorder within the target's parent
      toParent = targetNode.parent_id || null;
      if (toParent && banned.has(toParent)) return; // parent inside dragged subtree
      const sibs = (byParent[toParent || 'root'] || []).filter((n) => n.id !== draggedId);
      const j = sibs.findIndex((n) => n.id === targetNode.id);
      toIndex = j < 0 ? sibs.length : j;
    }
    try {
      const { data } = await notesApi.post(`/notes/tasks/tasks/${taskId}/subtasks/${draggedId}/move`, { to_parent_id: toParent, to_index: toIndex });
      apply(data);
    } catch { /* noop */ }
  }, [byParent, taskId, apply]);

  const onDragStart = useCallback((e, node) => {
    dragIdRef.current = node.id;
    setDragId(node.id);
    try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', node.id); } catch { /* ignore */ }
  }, []);
  const onDragEnd = useCallback(() => { dragIdRef.current = null; setDragId(null); setDropHint(null); }, []);
  const onBeforeOver = useCallback((e, node) => { if (!dragIdRef.current) return; e.preventDefault(); setDropHint({ id: node.id, mode: 'before' }); }, []);
  const onBeforeDrop = useCallback((node) => { const d = dragIdRef.current; dragIdRef.current = null; setDragId(null); setDropHint(null); doMove(d, node, 'before'); }, [doMove]);
  const onRowOver = useCallback((e, node) => { const d = dragIdRef.current; if (!d || d === node.id) return; e.preventDefault(); setDropHint({ id: node.id, mode: 'nest' }); }, []);
  const onRowLeave = useCallback(() => { /* keep last hint until a new over fires */ }, []);
  const onRowDrop = useCallback((node) => { const d = dragIdRef.current; dragIdRef.current = null; setDragId(null); setDropHint(null); doMove(d, node, 'nest'); }, [doMove]);

  const renderRows = () => rows.map(({ node, depth, hasKids }) => (
    <SubtaskRow key={node.id} node={node} depth={depth} hasKids={hasKids}
      isCollapsed={collapsed.has(node.id)} canEdit={canEdit} compact={compact}
      isEditing={editingId === node.id} editValue={editValue} setEditValue={setEditValue}
      onSubmitEdit={submitEdit} onCancelEdit={cancelEdit} onOpenEdit={openEdit}
      isAdding={addingUnder === node.id} addValue={addValue} setAddValue={setAddValue}
      onSubmitAdd={submitAdd} onCancelAdd={cancelAdd} onOpenAdd={openAdd}
      onToggle={toggle} onToggleCollapse={toggleCollapse} onDelete={del}
      draggable={canEdit && !compact && editingId !== node.id}
      isDragging={dragId === node.id}
      dropHint={dropHint && dropHint.id === node.id ? dropHint.mode : null}
      onDragStart={onDragStart} onDragEnd={onDragEnd}
      onBeforeOver={onBeforeOver} onBeforeDrop={onBeforeDrop}
      onRowOver={onRowOver} onRowLeave={onRowLeave} onRowDrop={onRowDrop} />
  ));

  // --- Compact (inline in a list row) --------------------------------------
  if (compact) {
    if (loading) return <div className="pl-8 pr-3 py-2 text-[12px] text-[var(--n-muted-2)]" data-testid="task-subtasks-compact-loading">Loading sub-tasks…</div>;
    if (rootCount === 0 && !allowAdd) return null;
    return (
      <div className="mt-1 rounded-xl border border-[var(--n-border)] bg-[var(--n-canvas-2)]/40 px-2 py-1.5" data-testid="task-subtasks-compact">
        <div className="mb-1 flex items-center justify-between px-1">
          <span className="text-[11px] font-medium tabular-nums text-[var(--n-muted-2)]" data-testid="task-subtasks-compact-count">{total > 0 ? `${done}/${total} done` : 'No sub-tasks yet'}</span>
          {canEdit && total > 0 && (
            <button type="button" data-testid="task-subtasks-complete-all"
              onClick={() => setAll(!allDone)}
              title={allDone ? 'Mark all sub-tasks incomplete' : 'Mark all sub-tasks complete'}
              className="n-focus inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-[var(--n-accent)] transition-colors hover:bg-[var(--n-accent-bg)]">
              <CheckCheck size={12} />{allDone ? 'Reopen all' : 'Complete all'}
            </button>
          )}
        </div>
        {total > 0 && (
          <div className="mx-1 mb-1.5 h-1 overflow-hidden rounded-full bg-[var(--n-border)]" role="progressbar"
            aria-valuenow={done} aria-valuemin={0} aria-valuemax={total} data-testid="task-subtasks-progress">
            <div className={`h-full rounded-full transition-[width] duration-200 ${allDone ? 'bg-[var(--n-success,var(--n-accent))]' : 'bg-[var(--n-accent)]'}`}
              style={{ width: `${total > 0 ? Math.round((done / total) * 100) : 0}%` }} />
          </div>
        )}
        {renderRows()}
        {allowAdd && canEdit && (
          <div className="mt-1 flex items-center gap-1.5 px-1" style={{ paddingLeft: '4px' }}>
            <Plus size={14} className="shrink-0 text-[var(--n-muted-2)]" />
            <input value={rootValue} placeholder="Add a sub-task…" data-testid="task-subtasks-compact-add-input"
              onChange={(e) => setRootValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitRoot(); else if (e.key === 'Escape') setRootValue(''); }}
              className="n-focus min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1 py-1 text-[13px] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)] hover:border-[var(--n-border)] focus:border-[var(--n-border)] focus:bg-[var(--n-canvas-2)]" />
            {rootValue.trim() && (
              <button type="button" data-testid="task-subtasks-compact-add-button" onClick={submitRoot}
                className="n-focus rounded-md bg-[var(--n-accent)] px-2 py-1 text-[12px] font-semibold text-white hover:opacity-90">Add</button>
            )}
          </div>
        )}
      </div>
    );
  }

  // --- Full (detail panel) -------------------------------------------------
  return (
    <div data-testid="task-subtasks">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">
          <ListTree size={13} className="text-[var(--n-accent)]" /> Sub-tasks
        </div>
        {total > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-[11.5px] font-medium text-[var(--n-muted)]" data-testid="task-subtasks-count">Completed {done} of {total}</span>
            {canEdit && (
              <button type="button" data-testid="task-subtasks-complete-all"
                onClick={() => setAll(!allDone)}
                title={allDone ? 'Mark all sub-tasks incomplete' : 'Mark all sub-tasks complete'}
                className="n-focus inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-[var(--n-accent)] transition-colors hover:bg-[var(--n-accent-bg)]">
                <CheckCheck size={12} />{allDone ? 'Reopen all' : 'Complete all'}
              </button>
            )}
          </div>
        )}
      </div>

      {total > 0 && (
        <div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-[var(--n-canvas-2)]" data-testid="task-subtasks-progress"
          role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full rounded-full bg-[var(--n-accent)] transition-[width] duration-200" style={{ width: `${pct}%` }} />
        </div>
      )}

      {loading ? (
        <div className="text-[12px] text-[var(--n-muted-2)]" data-testid="task-subtasks-loading">Loading…</div>
      ) : (
        <div className="space-y-0.5">
          {rootCount === 0 && (
            <p className="py-1 text-[12.5px] text-[var(--n-muted-2)]" data-testid="task-subtasks-empty">
              {canEdit ? 'Break this task into smaller steps.' : 'No sub-tasks.'}
            </p>
          )}
          {renderRows()}
        </div>
      )}

      {canEdit && (
        <div className="mt-2 flex items-center gap-1.5" style={{ paddingLeft: '20px' }}>
          <Plus size={14} className="shrink-0 text-[var(--n-muted-2)]" />
          <input value={rootValue} placeholder="Add a sub-task…" data-testid="task-subtasks-add-input"
            onChange={(e) => setRootValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitRoot(); }}
            className="n-focus min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1 py-1 text-[13px] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)] hover:border-[var(--n-border)] focus:border-[var(--n-border)] focus:bg-[var(--n-canvas-2)]" />
          {rootValue.trim() && (
            <button type="button" data-testid="task-subtasks-add-button" onClick={submitRoot}
              className="n-focus rounded-md bg-[var(--n-accent)] px-2 py-1 text-[12px] font-semibold text-white hover:opacity-90">Add</button>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskSubtasks;
